import React from 'react';
import { ChevronDown, Settings, LifeBuoy, LogOut, User, Check } from 'lucide-react';

import { Avatar, AvatarFallback } from 'components/ui/avatar';
import { Button } from 'components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from 'components/ui/dropdown-menu';
import { AdminAccessLogo } from './AdminAccessLogo';
import { AdminAccessSummary } from './planTypes';
import { adminMenuLabel } from './planDisplayHelpers';
import { AccionesDeCuenta, TEXTOS_DE_NAVEGACION, TextosDeNavegacion } from './navTextos';

/**
 * Pieza del Sidebar: menú de usuario (abajo del todo, se abre hacia arriba,
 * estilo Notion — mismo criterio real, ver comentario de
 * DesktopSidebarV2.tsx: "antes el avatar/nombre iba arriba y Soporte/Cerrar
 * sesión en un footer aparte; ahora vive todo aquí abajo, junto con
 * Personal/Admin", que antes estaba anidado en el selector de plan de
 * arriba — el acceso Admin es un aspecto de USUARIO, no un plan).
 *
 * Recreación anterior (antes de esta revisión) se dejaba el bloque
 * Personal/Admin fuera — sin él, este menú no reflejaba de verdad el
 * componente real, solo su mitad de "ajustes". Se añade aquí completo.
 */
export interface SidebarUserMenuProps {
	userName: string;
	userEmail: string;
	isMenuOpen: boolean;
	/** Permite ocultar el switch aunque haya acceso de administración. No hace
	 * falta para el caso contrario: sin `adminAccess` el switch no sale, porque
	 * solo tiene sentido si hay algo entre lo que elegir — mismo criterio que
	 * showPersonalAdminSwitch real. */
	showPersonalAdminSwitch?: boolean;
	isAdminActive: boolean;
	/** Empresas que administra, para la etiqueta del switch. */
	adminAccess?: AdminAccessSummary | null;
	onSelectPersonal: () => void;
	onSelectAdmin: () => void;
	/**
	 * Configuración, Soporte y Cerrar sesión. Sin pasarlo, las tres filas se enseñan sin hacer nada;
	 * pasándolo, cada una sale solo si tiene a dónde llevar (ver `AccionesDeCuenta`).
	 */
	cuenta?: AccionesDeCuenta;
	textos?: Pick<
		TextosDeNavegacion,
		| 'personal'
		| 'adminDeUnaEmpresa'
		| 'adminDeVariasEmpresas'
		| 'menuDeUsuario'
		| 'configuracion'
		| 'soporte'
		| 'cerrarSesion'
	>;
}

export const SidebarUserMenu = ({
	userName,
	userEmail,
	isMenuOpen,
	showPersonalAdminSwitch = true,
	isAdminActive,
	adminAccess = null,
	onSelectPersonal,
	onSelectAdmin,
	cuenta,
	textos = TEXTOS_DE_NAVEGACION
}: SidebarUserMenuProps) => {
	const initials = userName
		.split(' ')
		.map((n) => n[0])
		.slice(0, 2)
		.join('');
	// Sin empresas administradas no hay switch: la fila de Admin saldría con su
	// logo y sin etiqueta, y al pulsarla escondería el selector de plan.
	const muestraSwitch = showPersonalAdminSwitch && (adminAccess?.companies.length ?? 0) > 0;
	const muestraConfiguracion = !cuenta || !!cuenta.onOpenSettings;
	const muestraSoporte = !cuenta || !!cuenta.supportHref;
	const muestraCerrarSesion = !cuenta || !!cuenta.onLogout;
	// Una línea separa grupos: solo si queda algo a los dos lados (nunca dos seguidas ni una suelta).
	const hayEnMedio = muestraConfiguracion || muestraSoporte;
	const lineaTrasElRol = hayEnMedio || muestraCerrarSesion;

	return (
		// dark:border-sidebar-border, no dark:border-border: este borde separa el
		// footer del propio sidebar (--sidebar-background), no vive sobre --card —
		// ver el mismo criterio en SidebarPlanSelector.tsx. El DropdownMenuContent
		// de más abajo SÍ vive sobre --card, así que ese conserva --border.
		<div
			className={`border-t border-gray-200 dark:border-sidebar-border p-3 ${!isMenuOpen ? 'p-2' : ''}`}
		>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button
						variant="ghost"
						className={`w-full flex items-center gap-2.5 h-auto rounded-control px-2 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-700 dark:text-foreground dark:hover:bg-card transition-colors ${
							!isMenuOpen ? 'justify-center px-0' : ''
						}`}
					>
						<Avatar className="h-8 w-8 flex-none">
							<AvatarFallback className="bg-sidebar-accent text-sidebar-accent-foreground text-xs font-medium">
								{initials}
							</AvatarFallback>
						</Avatar>
						{isMenuOpen && (
							<>
								<span className="min-w-0 flex-1 text-left">
									<span className="block truncate text-gray-900 dark:text-foreground">
										{userName}
									</span>
									<span className="block truncate text-xs font-normal text-gray-500 dark:text-muted-foreground">
										{userEmail}
									</span>
								</span>
								<ChevronDown className="h-4 w-4 flex-none text-gray-400 dark:text-muted-foreground" />
							</>
						)}
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent
					className="w-64 bg-white border border-gray-200 dark:bg-card dark:border-border"
					align="start"
					side={isMenuOpen ? 'top' : 'right'}
					sideOffset={4}
					aria-label={textos.menuDeUsuario}
				>
					{muestraSwitch && (
						<div
							className={
								lineaTrasElRol ? 'mb-1 border-b border-gray-100 pb-1 dark:border-border' : undefined
							}
						>
							<DropdownMenuItem
								onSelect={onSelectPersonal}
								className="flex cursor-pointer items-center gap-2 py-1.5 hover:bg-gray-50 focus:bg-gray-50 focus:text-inherit dark:hover:bg-card dark:focus:bg-card dark:focus:text-inherit"
							>
								<span className="flex h-6 w-6 flex-none items-center justify-center rounded-full border border-gray-100 bg-white dark:border-border dark:bg-card">
									<User className="h-3 w-3 text-arca-green" />
								</span>
								<span className="min-w-0 flex-1 truncate text-sm text-gray-900 dark:text-foreground">
									{textos.personal}
								</span>
								{!isAdminActive && (
									<Check
										className="h-4 w-4 flex-none text-arca-green dark:text-primary"
										data-testid="personal-selected-check"
									/>
								)}
							</DropdownMenuItem>
							<DropdownMenuItem
								onSelect={onSelectAdmin}
								className="flex cursor-pointer items-center gap-2 py-1.5 hover:bg-gray-50 focus:bg-gray-50 focus:text-inherit dark:hover:bg-card dark:focus:bg-card dark:focus:text-inherit"
							>
								<AdminAccessLogo className="h-6 w-6 rounded-full" />
								<span className="min-w-0 flex-1 truncate text-sm text-gray-900 dark:text-foreground">
									{adminAccess && adminMenuLabel(adminAccess, textos)}
								</span>
								{isAdminActive && (
									<Check
										className="h-4 w-4 flex-none text-arca-green dark:text-primary"
										data-testid="admin-selected-check"
									/>
								)}
							</DropdownMenuItem>
						</div>
					)}

					{muestraConfiguracion && (
						<DropdownMenuItem
							onSelect={cuenta?.onOpenSettings}
							className="flex cursor-pointer items-center gap-2 py-1.5 dark:text-foreground dark:focus:bg-accent"
						>
							<Settings className="h-4 w-4 text-gray-500 dark:text-muted-foreground" />
							<span>{textos.configuracion}</span>
						</DropdownMenuItem>
					)}
					{muestraSoporte &&
						(cuenta?.supportHref ? (
							// Una página aparte: un enlace de verdad, que se abre en otra pestaña.
							<DropdownMenuItem
								asChild
								className="flex cursor-pointer items-center gap-2 py-1.5 dark:text-foreground dark:focus:bg-accent"
							>
								<a href={cuenta.supportHref} target="_blank" rel="noopener noreferrer">
									<LifeBuoy className="h-4 w-4 text-gray-500 dark:text-muted-foreground" />
									<span>{textos.soporte}</span>
								</a>
							</DropdownMenuItem>
						) : (
							<DropdownMenuItem className="flex cursor-pointer items-center gap-2 py-1.5 dark:text-foreground dark:focus:bg-accent">
								<LifeBuoy className="h-4 w-4 text-gray-500 dark:text-muted-foreground" />
								<span>{textos.soporte}</span>
							</DropdownMenuItem>
						))}
					{muestraCerrarSesion && (
						<div
							className={
								hayEnMedio ? 'mt-1 border-t border-gray-100 pt-1 dark:border-border' : undefined
							}
						>
							<DropdownMenuItem
								onSelect={cuenta?.onLogout}
								className="flex cursor-pointer items-center gap-2 py-1.5 text-gray-600 dark:text-muted-foreground dark:focus:bg-accent"
							>
								<LogOut className="h-4 w-4" />
								<span>{textos.cerrarSesion}</span>
							</DropdownMenuItem>
						</div>
					)}
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
};
